"use client"

import { useState, useEffect, useRef } from "react"
import { Link, useNavigate } from "react-router-dom"
import { apiConfig } from "../config/api.config"
import logoAlcaldia from "../assets/logocopacabana.png"
import { LogOut, Plus, FileText, User, Trash2, Edit, Calendar } from "lucide-react"
import PresupuestoSolicitudesModal from "../components/PresupuestoSolicitudesModal"
import AlmacenCertificadoModal from "../components/AlmacenCertificadoModal"
import BackToMenu from "../components/BackToMenu.jsx"
import { useAuth } from "../context/AuthContext"
import EnviarAlmacen from "../components/EnviarAlmacen"

const API_BASE_URL =
  import.meta.env.VITE_API_BASE_URL ||
  (window.location.hostname === "localhost" ? "http://localhost:3001" : window.location.origin)

console.log("🔧 API_BASE_URL:", API_BASE_URL)

// 🔹 Mapeo de id de rol (BD) → nombre interno
const ROLE_MAP = {
  1: "solicitante",
  2: "planeacion",
  3: "hacienda",
  4: "juridica",
  5: "alcalde",
  6: "administrador",
  7: "presupuesto",
  8: "almacen",
  9: "validador",
  10: "firma_secretario",
  11: "firma_alcalde",
}

const ROLE_LABELS = {
  solicitante: "Solicitante",
  planeacion: "Planeación",
  hacienda: "Hacienda",
  juridica: "Jurídica",
  alcalde: "Alcalde",
  administrador: "Administrador",
  presupuesto: "Presupuesto",
  almacen: "Almacén",
  validador: "Validador (Inexistencia)",
  firma_secretario: "Secretario (Inexistencia)",
  firma_alcalde: "Alcalde (Inexistencia)",
}

// 🔹 Normalizador de status (BD → formato interno)
const normalizeStatus = (status) =>
  String(status || "")
    .trim()
    .toLowerCase()
    .replace(/\s+/g, "_")

const isPendienteAprobacionAlcalde = (contract) => {
  const phase = Number(contract?.current_phase || 0)
  const st = normalizeStatus(contract?.status)
  return phase === 4 && (st === "en_proceso" || st === "en_tramite" || st === "en_trámite")
}

// ✅ Botón Solicitudes: colores (CDP amarillo, RP verde, mixto azul, default gris)
const getSolicitudesButtonStyle = (contract) => {
  const hasCDP = Boolean(contract?.tiene_cdp)
  const hasRP = Boolean(contract?.tiene_rp)

  if (hasCDP && hasRP) return { backgroundColor: "#1e40af", color: "white" } // mixto
  if (hasCDP) return { backgroundColor: "#FACC15", color: "#854D0E" } // CDP
  if (hasRP) return { backgroundColor: "#22C55E", color: "#166534" } // RP

  return { backgroundColor: "#6b7280", color: "white" } // sin info
}

// 🔹 Normaliza campos que pueden venir con nombres distintos desde la API
function normalizeContractRow(row) {
  const id =
    row.id ??
    row.contract_id ??
    row.contractId ??
    row.contractID ??
    row.uuid ??
    row.contract_uuid ??
    row.contractUuid ??
    row.contractUUID ??
    null

  const nombre_contratista =
    row.nombre_contratista ??
    row.contratista_nombre ??
    row.contratista ??
    row.nombreContratista ??
    row.contractor_name ??
    row.nombre_contratista_detalle ??
    ""

  const secretaria =
    row.secretaria ??
    row.dependencia ??
    row.secretary ??
    row.dependencia_contratante ??
    ""

  const status =
    row.status ??
    row.estado ??
    row.estado_contrato ??
    row.estadoActual ??
    row.status_contrato ??
    row.state ??
    ""

  const rawPhase =
    row.current_phase ??
    row.fase_actual ??
    row.fase ??
    row.phase ??
    row.fase_contrato ??
    1

  const current_phase = Number(rawPhase) || 1

  return {
    ...row,
    id,
    nombre_contratista,
    secretaria,
    status,
    current_phase,
  }
}

export default function Dashboard() {
  const [contracts, setContracts] = useState([])
  const [contractsLoading, setContractsLoading] = useState(true)
  const [error, setError] = useState(null)

  const [searchVisible, setSearchVisible] = useState(false)
  const [searchTerm, setSearchTerm] = useState("")
  const [deleteConfirm, setDeleteConfirm] = useState(null)

  const [windowSize, setWindowSize] = useState({
    width: typeof window !== "undefined" ? window.innerWidth : 0,
    height: typeof window !== "undefined" ? window.innerHeight : 0,
  })

  const [showPhaseHistoryModal, setShowPhaseHistoryModal] = useState(false)
  const [selectedContractHistory, setSelectedContractHistory] = useState([])
  const [selectedContractName, setSelectedContractName] = useState("")

  const [showSolicitudModal, setShowSolicitudModal] = useState(false)
  const [selectedContract, setSelectedContract] = useState(null)
  const [showResponderAlmacen, setShowResponderAlmacen] = useState(false)
  const [solicitudSeleccionada, setSolicitudSeleccionada] = useState(null)

  const [phaseModalOpen, setPhaseModalOpen] = useState(false)
  const [phaseModalRows, setPhaseModalRows] = useState([])
  const [phaseModalNombre, setPhaseModalNombre] = useState("")
  const [phaseModalTotal, setPhaseModalTotal] = useState(0)

  const [showAlmacenCertModal, setShowAlmacenCertModal] = useState(false)
  const [selectedContractForAlmacen, setSelectedContractForAlmacen] = useState(null)
  const [selectedAlmacenRequest, setSelectedAlmacenRequest] = useState(null)

  // ✅ evita setState luego de un unmount y evita carreras
  const isMountedRef = useRef(true)
  useEffect(() => {
    isMountedRef.current = true
    return () => {
      isMountedRef.current = false
    }
  }, [])

  const navigate = useNavigate()

  // 🔐 Auth desde contexto
  const { user, userRole: rawUserRole, loading: authLoading, logout } = useAuth()

  // 🔹 userRole normalizado a string
  const role = (() => {
    const v = rawUserRole
    const n = Number(v)
    if (!Number.isNaN(n) && ROLE_MAP[n]) return ROLE_MAP[n]
    return String(v || "").trim().toLowerCase()
  })()

  const roleLabel = ROLE_LABELS[role] || rawUserRole || "Sin rol"

  const authorizedEmails = [
    "comunicaciones@copacabana.gov.co",
    "alejandra.valbuena@copacabana.gov.co",
    "juridica@copacabana.gov.co",
    "dianasimpac@gmail.com",
  ]

  const isAuthorizedUser = (email) => authorizedEmails.includes(email)

  useEffect(() => {
    function handleResize() {
      setWindowSize({
        width: window.innerWidth,
        height: window.innerHeight,
      })
    }
    window.addEventListener("resize", handleResize)
    handleResize()
    return () => window.removeEventListener("resize", handleResize)
  }, [])

  // ---------- UTILIDADES DE HISTORIAL / FASES ----------

  const calculateExactDays = (startDate, endDate) => {
    if (!startDate || !endDate) return 0
    const start = new Date(startDate)
    const end = new Date(endDate)
    start.setHours(0, 0, 0, 0)
    end.setHours(0, 0, 0, 0)
    if (start.getTime() === end.getTime()) return 0
    const diffTime = Math.abs(end.getTime() - start.getTime())
    const diffDays = Math.round(diffTime / 86400000)
    return diffDays
  }

  const processPhaseHistory = (historyData, contractData) => {
    const phaseNames = { 1: "Planeación", 2: "Hacienda", 3: "Jurídica", 4: "Alcalde", 5: "Aprobado" }

    const creationDate = new Date(contractData.created_at)
    const statusNorm = normalizeStatus(contractData?.status)
    const isApproved = statusNorm === "aprobado"
    const isRejected = statusNorm === "rechazado"
    const currentPhase = contractData.current_phase

    const sortedHistory =
      Array.isArray(historyData) && historyData.length > 0
        ? [...historyData].sort((a, b) => new Date(a.created_at) - new Date(b.created_at))
        : []

    const phaseTransitions = []
    phaseTransitions.push({ phase: 1, date: creationDate, type: "start" })

    if (sortedHistory.length > 0) {
      sortedHistory.forEach((entry) => {
        phaseTransitions.push({ phase: entry.phase, date: new Date(entry.created_at), type: "transition" })
      })
    }

    if (isApproved) {
      const approvalDate = new Date(contractData.updated_at)
      phaseTransitions.push({ phase: 5, date: approvalDate, type: "approval" })
    }

    phaseTransitions.sort((a, b) => a.date - b.date)

    const phaseStartEnd = {}
    let lastPhase = 1

    for (let i = 0; i < phaseTransitions.length; i++) {
      const transition = phaseTransitions[i]
      const phase = transition.phase

      if (i === 0 || phase !== lastPhase) {
        if (!phaseStartEnd[phase]) {
          phaseStartEnd[phase] = { start: transition.date, end: null }
        }
        if (i > 0 && lastPhase !== phase) {
          if (phaseStartEnd[lastPhase]) phaseStartEnd[lastPhase].end = transition.date
        }
        lastPhase = phase
      }
    }

    const endDate = isApproved || isRejected ? new Date(contractData.updated_at) : new Date()
    if (phaseStartEnd[lastPhase]) phaseStartEnd[lastPhase].end = endDate

    for (let phase = 1; phase <= Math.max(currentPhase, 5); phase++) {
      if (!phaseStartEnd[phase] && phase <= currentPhase) {
        let prevPhase = phase - 1
        while (prevPhase >= 1 && !phaseStartEnd[prevPhase]) prevPhase--

        let nextPhase = phase + 1
        while (nextPhase <= currentPhase && !phaseStartEnd[nextPhase]) nextPhase++

        const start = prevPhase >= 1 && phaseStartEnd[prevPhase] ? phaseStartEnd[prevPhase].end : creationDate
        const end = nextPhase <= currentPhase && phaseStartEnd[nextPhase] ? phaseStartEnd[nextPhase].start : endDate

        phaseStartEnd[phase] = { start, end }
      }
    }

    const processedHistory = []
    for (const phase in phaseStartEnd) {
      if (Object.prototype.hasOwnProperty.call(phaseStartEnd, phase)) {
        const { start, end } = phaseStartEnd[phase]
        const days = calculateExactDays(start, end)
        processedHistory.push({
          phase: Number.parseInt(phase),
          phase_name: phaseNames[phase] || `Fase ${phase}`,
          start_date: start.toISOString(),
          end_date: end.toISOString(),
          days,
        })
      }
    }

    processedHistory.sort((a, b) => a.phase - b.phase)
    return processedHistory
  }

  const formatDate = (value) => {
    if (!value) return "Sin fecha"

    if (value instanceof Date && !Number.isNaN(value.getTime())) {
      return value.toLocaleDateString("es-CO")
    }

    const str = String(value).trim()

    const mDateOnly = str.match(/^(\d{4})-(\d{2})-(\d{2})(?:T00:00:00(?:\.000)?(?:Z)?)?$/)
    if (mDateOnly) {
      const y = Number(mDateOnly[1])
      const mo = Number(mDateOnly[2]) - 1
      const d = Number(mDateOnly[3])
      return new Date(y, mo, d).toLocaleDateString("es-CO")
    }

    const meses = {
      jan: 1,
      feb: 2,
      mar: 3,
      apr: 4,
      may: 5,
      jun: 6,
      jul: 7,
      aug: 8,
      sep: 9,
      oct: 10,
      nov: 11,
      dec: 12,
    }
    const m = str.match(/^([A-Za-z]{3})\s+(\d{1,2})\s+(\d{4})/)
    if (m) {
      const mes = meses[m[1].toLowerCase()]
      const dia = parseInt(m[2], 10)
      const anio = parseInt(m[3], 10)
      if (mes) return new Date(anio, mes - 1, dia).toLocaleDateString("es-CO")
    }

    const dt = new Date(str)
    if (Number.isNaN(dt.getTime())) return str

    return dt.toLocaleDateString("es-CO", { timeZone: "America/Bogota" })
  }

  // ---------- CARGA DE CONTRATOS (SOLO API) ----------

  const fetchContracts = async (currentRole = role, currentUser = user) => {
    if (!isMountedRef.current) return

    setContractsLoading(true)
    setError(null)

    try {
      const baseUrl = apiConfig.getUrl(apiConfig.endpoints.contracts)
      const response = await fetch(baseUrl, {
        credentials: "include",
        headers: { Accept: "application/json" },
      })

      if (!response.ok) {
        const text = await response.text()
        console.error("❌ Respuesta no OK al listar contratos:", response.status, text)
        throw new Error(`Error HTTP ${response.status}`)
      }

      let data = await response.json()

      if (data && data.contracts && Array.isArray(data.contracts)) {
        data = data.contracts
      } else if (!Array.isArray(data)) {
        data = []
      }

      console.log("📦 Contracts RAW from API:", data[0])

      let normalized = data.map(normalizeContractRow)
      normalized = normalized.filter((c) => c && c.id)

      console.log("✅ Contracts normalizados (primer registro):", normalized[0])

      // 🔹 Filtros por rol
      if (currentRole === "administrador" || currentRole === "juridica") {
        // ven todos
      } else if (currentRole === "solicitante" && currentUser) {
        console.log("⚠️ FILTRO DE SOLICITANTE DESACTIVADO - Mostrando todos")
        console.log("User ID:", currentUser.id)
        console.log("Total contratos antes de filtrar:", normalized.length)
      } else if (currentRole === "planeacion") {
        normalized = normalized.filter((contract) => {
          const phase = Number(contract.current_phase || 0)
          const statusNorm = normalizeStatus(contract.status)
          return phase === 1 && statusNorm === "en_proceso"
        })
      } else if (currentRole === "hacienda") {
        normalized = normalized.filter(
          (contract) => contract.current_phase === 2 && normalizeStatus(contract.status) === "en_proceso",
        )
      } else if (currentRole === "alcalde") {
        normalized = normalized.filter(
          (contract) => contract.current_phase === 4 && normalizeStatus(contract.status) === "en_proceso",
        )

        console.log(
          "🟡 TODOS los contratos (phase/status):",
          normalized.map((c) => ({
            id: c.id,
            radicado: c.radicado,
            phase: c.current_phase,
            status: c.status,
            statusNorm: normalizeStatus(c.status),
          })),
        )
      } else if (currentRole === "presupuesto") {
        console.log("🔵 Cargando contratos para PRESUPUESTO...")

        try {
          const urlPending = apiConfig.getUrl("/api/presupuesto/solicitudes/all-pending.php")
          console.log("📡 URL solicitudes pendientes:", urlPending)

          const respPending = await fetch(urlPending, {
            credentials: "include",
            headers: { Accept: "application/json" },
          })

          if (!respPending.ok) {
            console.error("❌ Error obteniendo solicitudes pendientes:", respPending.status)
            normalized = []
          } else {
            const dataPending = await respPending.json()
            const solicitudesPendientes = dataPending.solicitudes || []

            console.log("✅ Solicitudes CDP/RP pendientes:", solicitudesPendientes.length)

            // ✅ FIX CLAVE: comparar SIEMPRE por string normalizado (evita mismatch number/string)
            const normalizeId = (v) => String(v ?? "").trim()

            const getContractIdFromSolicitud = (s) => {
              const raw =
                s?.contract_id ??
                s?.contractId ??
                s?.contractID ??
                s?.contract_uuid ??
                s?.contractUuid ??
                ""
              return normalizeId(raw)
            }

            // { "123": {cdp:true,rp:false}, ... }
            const flagsByContract = new Map()

            for (const s of solicitudesPendientes) {
              const cid = getContractIdFromSolicitud(s)
              const tipo = String(s.request_type || "").toUpperCase().trim()
              if (!cid) continue

              const prev = flagsByContract.get(cid) || { cdp: false, rp: false }
              if (tipo === "CDP") prev.cdp = true
              if (tipo === "RP") prev.rp = true
              flagsByContract.set(cid, prev)
            }

            // ✅ Filtrar contratos que tengan solicitudes (mismo tipo de key)
            normalized = normalized.filter((c) => flagsByContract.has(normalizeId(c.id)))

            // ✅ Enriquecer flags para pintar el botón
            normalized = normalized.map((c) => {
              const f = flagsByContract.get(normalizeId(c.id)) || { cdp: false, rp: false }
              return { ...c, tiene_cdp: f.cdp, tiene_rp: f.rp }
            })

            console.log("✅ Contratos visibles para Presupuesto:", normalized.length)

            normalized.sort((a, b) => {
              const dateA = new Date(a.created_at || 0).getTime()
              const dateB = new Date(b.created_at || 0).getTime()
              return dateA - dateB
            })
          }
        } catch (error) {
          console.error("❌ Error en filtro de Presupuesto:", error)
          normalized = []
        }
      } else if (currentRole === "almacen") {
        // 1) Solo contratos en fase 5 (como ya lo tienes)
        const candidatos = normalized.filter((contract) => {
          const phase = Number(contract?.current_phase || 0)
          return phase === 5
        })

        const withRequests = []

        // helper: normaliza respuesta del endpoint (puede venir array o {data:[]})
        const toArray = (raw) => {
          if (Array.isArray(raw)) return raw
          if (raw && Array.isArray(raw.data)) return raw.data
          if (raw && Array.isArray(raw.rows)) return raw.rows
          if (raw && typeof raw === "object") return [raw]
          return []
        }

        for (const contract of candidatos) {
          try {
            const solicitudesUrl = apiConfig.getUrl(
              apiConfig.endpoints.almacenSolicitudes,
              `contract_id=${encodeURIComponent(contract.id)}`,
            )

            const resp = await fetch(solicitudesUrl, {
              credentials: "include",
              headers: { Accept: "application/json" },
            })

            if (!resp.ok) continue

            // ✅ IMPORTANTÍSIMO: SOLO una vez json()
            const raw = await resp.json().catch(() => null)
            const solicitudesArray = toArray(raw)

            const pendientes = solicitudesArray.filter((s) => {
              const responded = Number(s?.responded ?? 0) === 1
              if (responded) return false

              const rs = String(s?.response_status ?? "").trim().toLowerCase()
              const st = String(s?.status ?? "").trim().toLowerCase()

              const isFinal = (v) => {
                const x = String(v ?? "").trim().toLowerCase()
                return x === "aprobado" || x === "negado" || x === "rechazado" || x === "approved" || x === "denied"
              }

              const isEmptyOrPending = (v) => {
                const x = String(v ?? "").trim().toLowerCase()
                return !x || x === "pending" || x === "pendiente"
              }

              // si cualquiera ya está final -> no pendiente
              if (isFinal(rs) || isFinal(st)) return false

              // pendiente si ambos están vacío/pending
              return isEmptyOrPending(rs) && isEmptyOrPending(st)
            })

            if (pendientes.length > 0) {
              withRequests.push({
                ...contract,
                almacen_requests: pendientes,
              })
            }
          } catch (e) {
            console.error("[ALMACEN] error consultando solicitudes", contract?.id, e)
          }
        }

        normalized = withRequests

        // opcional: orden por fecha de envío/creación de la solicitud
        normalized.sort((a, b) => {
          const sA = a.almacen_requests?.[0]
          const sB = b.almacen_requests?.[0]
          const dA = sA ? new Date(sA.sent_at || sA.created_at || 0).getTime() : 0
          const dB = sB ? new Date(sB.sent_at || sB.created_at || 0).getTime() : 0
          return dA - dB
        })
      }

      // 🔹 Ordenar SIEMPRE en orden de llegada
      normalized.sort((a, b) => {
        const da = new Date(a.fecha_solicitud || a.created_at || 0)
        const db = new Date(b.fecha_solicitud || b.created_at || 0)

        if (da.getTime() === db.getTime()) {
          const ca = new Date(a.created_at || 0)
          const cb = new Date(b.created_at || 0)

          if (!isNaN(ca.getTime()) && !isNaN(cb.getTime()) && ca.getTime() !== cb.getTime()) {
            return ca - cb
          }

          return String(a.id || "").localeCompare(String(b.id || ""))
        }

        return da - db
      })

      if (!isMountedRef.current) return
      setContracts(normalized)
    } catch (error) {
      console.error("Error fetching contracts:", error)
      if (!isMountedRef.current) return
      setError("Error al cargar los contratos")
      setContracts([])
    } finally {
      if (!isMountedRef.current) return
      setContractsLoading(false)
    }
  }

  const openPhaseModal = async (contract) => {
    try {
      const baseContractsUrl = apiConfig.getUrl(apiConfig.endpoints.contracts)

      const res = await fetch(`${baseContractsUrl}/${contract.id}/history`, {
        credentials: "include",
        headers: { Accept: "application/json" },
      })

      if (!res.ok) {
        const txt = await res.text().catch(() => "")
        throw new Error(`HTTP ${res.status} - ${txt}`)
      }

      const data = await res.json()

      setPhaseModalRows(Array.isArray(data.rows) ? data.rows : [])
      setPhaseModalNombre(data.nombre_contratista || contract.nombre_contratista || "")
      setPhaseModalTotal(Number(data.total_hasta_alcalde || 0))
      setPhaseModalOpen(true)
    } catch (e) {
      console.error("Error al obtener historial:", e)
      alert("No se pudo cargar el historial de fases.")
    }
  }

  // ---------- EFECTO: cuando auth está listo, cargo contratos ----------
  useEffect(() => {
    if (authLoading) return

    if (!user) {
      navigate("/login")
      return
    }

    if (role) {
      fetchContracts(role, user)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [authLoading, user, role])

  // ---------- ACCIONES: eliminar / solicitudes ----------
  const deleteContract = async (contractId) => {
    try {
      const baseUrl = apiConfig.getUrl(apiConfig.endpoints.contracts)

      const res = await fetch(`${baseUrl}/${contractId}`, {
        method: "DELETE",
        credentials: "include",
      })

      if (!res.ok) {
        throw new Error(`Error HTTP ${res.status}`)
      }

      setContracts((prev) => prev.filter((contract) => contract.id !== contractId))
      setDeleteConfirm(null)
    } catch (error) {
      console.error("Error completo al eliminar contrato:", error)
      setError("Error al eliminar el contrato: " + (error.message || "Error desconocido"))
    }
  }

  const handleViewRequests = (contract) => {
    setSelectedContract(contract)
    setShowSolicitudModal(true)
  }

  // ✅ Responder solicitud de almacén: endpoint correcto + cierre modal + refresco
  const handleResponderAlmacen = async (requestId, contract, extra = {}) => {
    try {
      const cid = String(contract?.id ?? contract?.contract_id ?? "").trim()
      const rid = String(requestId ?? contract?.almacen_requests?.[0]?.id ?? "").trim()

      if (!cid) {
        alert("Error: Falta contract_id del contrato.")
        return
      }

      // ✅ usa SIEMPRE el endpoint oficial de apiConfig (debe apuntar a .../index.php)
      const url = apiConfig.getUrl(apiConfig.endpoints.almacenSolicitudes)

      const payload = {
        contract_id: cid,
        request_id: rid || null,

        response_status: String(extra?.status || "aprobado").toLowerCase().trim(), // "aprobado" | "negado"
        motivo_rechazo: extra?.motivo ? String(extra.motivo) : null,

        response_attachments: Array.isArray(extra?.attachments) ? extra.attachments : [],
        notes: extra?.notes ? String(extra.notes) : "Respuesta enviada por almacén.",
      }

      const res = await fetch(url, {
        method: "PUT",
        credentials: "include",
        headers: { "Content-Type": "application/json", Accept: "application/json" },
        body: JSON.stringify(payload),
      })

      const data = await res.json().catch(() => ({}))

      if (!res.ok || data?.ok === false) {
        throw new Error(data?.error || data?.message || `Error HTTP ${res.status}`)
      }

      console.log("✅ Solicitud almacén respondida:", { cid, rid, status: payload.response_status })

      // ✅ cerrar modal (si está abierto)
      setShowSolicitudModal(false)

      // ✅ remover contrato del dashboard de almacén inmediatamente
      setContracts((prev) => prev.filter((c) => String(c.id) !== String(contract.id)))

      // ✅ refresco para consistencia total con backend
      await fetchContracts(role, user)
    } catch (error) {
      console.error("❌ Error en handleResponderAlmacen:", error)
      alert("Error al responder: " + (error.message || "Error desconocido"))
    }
  }

  // ---------- UI helpers ----------
  const getPhaseText = (phase, status) => {
    const statusNorm = normalizeStatus(status)

    if (statusNorm === "rechazado") {
      switch (phase) {
        case 1:
          return "Rechazado por Planeación"
        case 2:
          return "Rechazado por Hacienda"
        case 3:
          return "Rechazado por Jurídica"
        case 4:
          return "Rechazado por Alcalde"
        default:
          return "Rechazado"
      }
    }
    if (statusNorm === "aprobado") return "Aprobado por Alcalde"

    switch (phase) {
      case 1:
        return "Pendiente revisión Planeación"
      case 2:
        return "Pendiente revisión Hacienda"
      case 3:
        return "Pendiente revisión Jurídica"
      case 4:
        return "Pendiente aprobación Alcalde"
      case 5:
        return "Aprobado por Alcalde"
      default:
        return "Desconocido"
    }
  }

  const getStatusBadgeStyle = (status, contract) => {
    if (isPendienteAprobacionAlcalde(contract)) {
      return { backgroundColor: "#dbeafe", color: "#1e3a8a" }
    }

    const s = normalizeStatus(status)
    switch (s) {
      case "en_proceso":
      case "en_tramite":
      case "en_trámite":
        return { backgroundColor: "#fef9c3", color: "#854d0e" }
      case "aprobado":
        return { backgroundColor: "#dcfce7", color: "#166534" }
      case "rechazado":
        return { backgroundColor: "#fee2e2", color: "#b91c1c" }
      default:
        return { backgroundColor: "#f3f4f6", color: "#374151" }
    }
  }

  const getStatusLabel = (status) => {
    const s = normalizeStatus(status)
    switch (s) {
      case "en_proceso":
      case "en_tramite":
      case "en_trámite":
        return "En proceso"
      case "aprobado":
        return "Aprobado"
      case "rechazado":
        return "Rechazado"
      default:
        return status || "Desconocido"
    }
  }

  const canEditContract = (contract) => {
    if (role === "administrador") return true
    if (role === "solicitante") return contract.created_by === user?.id && contract.current_phase === 1
    if (role === "planeacion") return contract.current_phase === 1
    if (role === "hacienda") return contract.current_phase === 2
    if (role === "juridica") return contract.current_phase === 3
    if (role === "alcalde") return contract.current_phase === 4
    return false
  }

  const isMobile = windowSize.width < 768

  if (authLoading || contractsLoading) {
    return (
      <div
        style={{
          display: "flex",
          minHeight: "100vh",
          justifyContent: "center",
          alignItems: "center",
          background: "linear-gradient(to bottom,rgb(180, 194, 230),rgb(199, 204, 223))",
        }}
      >
        <div style={{ color: "white", fontSize: "1.25rem", fontWeight: "bold" }}>Cargando...</div>
      </div>
    )
  }

  const getColumnWidths = () => {
    if (isMobile) {
      return {
        fecha: "10%",
        secretaria: "12%",
        radicado: "8%",
        contratista: "14%",
        estado: "8%",
        fase: "14%",
        acciones: "12%",
        solicitudes: "12%",
      }
    }
    return {
      fecha: "10%",
      secretaria: "12%",
      radicado: "8%",
      contratista: "14%",
      estado: "8%",
      fase: "14%",
      acciones: "12%",
      solicitudes: "12%",
    }
  }

  const columnWidths = getColumnWidths()

  return (
    <div
      style={{
        display: "flex",
        flexDirection: "column",
        minHeight: "100vh",
        background: "linear-gradient(to bottom, #1e3a8a, #1e40af)",
        width: "100%",
        overflow: "hidden",
      }}
    >
      <div
        style={{
          flex: "1",
          padding: isMobile ? "1rem" : "2rem",
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          width: "100%",
          boxSizing: "border-box",
        }}
      >
        <div
          style={{
            maxWidth: role === "solicitante" ? "1000px" : "1200px",
            width: "100%",
            backgroundColor: "white",
            borderRadius: isMobile ? "0.5rem" : "1rem",
            boxShadow: "0 10px 25px rgba(0, 0, 0, 0.2)",
            overflow: "hidden",
          }}
        >
          {/* Barra superior: Volver al menú */}
          <div
            style={{
              padding: isMobile ? "0.75rem" : "1rem",
              borderBottom: "1px solid #e5e7eb",
              display: "flex",
              justifyContent: "flex-start",
              backgroundColor: "#f9fafb",
            }}
          >
            <BackToMenu label="Volver al menú" />
          </div>

          {/* Info usuario y acciones */}
          <div
            style={{
              padding: isMobile ? "1rem" : "1.5rem",
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              borderBottom: "1px solid #e5e7eb",
            }}
          >
            <img
              src={logoAlcaldia || "/placeholder.svg"}
              alt="Logo Alcaldía"
              style={{
                height: isMobile ? "120px" : "200px",
                width: "auto",
                maxWidth: "100%",
              }}
            />

            <h2
              style={{
                fontSize: isMobile ? "1.3rem" : "1.5rem",
                fontWeight: "bold",
                marginBottom: "0.5rem",
                color: "#1e3a8a",
                textAlign: "center",
              }}
            >
              Contratos
            </h2>

            <div
              style={{
                display: "flex",
                flexDirection: isMobile ? "column" : "row",
                alignItems: "center",
                justifyContent: "center",
                marginBottom: "1rem",
                width: "100%",
                gap: "10px",
              }}
            >
              <div
                style={{
                  display: "flex",
                  alignItems: "center",
                  backgroundColor: "#eff6ff",
                  padding: "0.5rem 1rem",
                  borderRadius: "9999px",
                  marginRight: isMobile ? "0" : "1rem",
                }}
              >
                <User size={16} style={{ marginRight: "0.5rem", color: "#1e40af" }} />
                <span style={{ fontSize: "0.875rem", color: "#1e40af" }}>
                  {user?.email} ({roleLabel})
                </span>
              </div>

              <button
                onClick={logout}
                style={{
                  display: "flex",
                  alignItems: "center",
                  backgroundColor: "#ef4444",
                  color: "white",
                  padding: "0.5rem 1rem",
                  borderRadius: "9999px",
                  border: "none",
                  cursor: "pointer",
                  fontSize: "0.875rem",
                  fontWeight: "500",
                }}
              >
                <LogOut size={16} style={{ marginRight: "0.5rem" }} />
                Cerrar sesión
              </button>
            </div>

            {(role === "solicitante" || role === "juridica") && (
              <Link
                to="/contratos/nuevo"
                style={{
                  display: "flex",
                  alignItems: "center",
                  backgroundColor: "#1e40af",
                  color: "white",
                  padding: "0.75rem 1.5rem",
                  borderRadius: "9999px",
                  textDecoration: "none",
                  fontWeight: "500",
                  boxShadow: "0 4px 6px -1px rgba(0, 0, 0, 0.1)",
                  marginBottom: "1rem",
                }}
              >
                <Plus size={18} style={{ marginRight: "0.5rem" }} />
                Nuevo Contrato
              </Link>
            )}

            {/* ✅ BOTONES SOLO ADMIN */}
            {role === "administrador" && (
              <div
                style={{
                  display: "flex",
                  flexDirection: isMobile ? "column" : "row",
                  gap: "8px",
                  width: "100%",
                  justifyContent: "center",
                  marginBottom: "0.5rem",
                  flexWrap: "wrap",
                }}
              >
                <button
                  type="button"
                  onClick={() =>
                    window.open(
                      `${API_BASE_URL}/api/export/db.php?role=${encodeURIComponent(role)}`,
                      "_blank",
                    )
                  }
                  style={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    backgroundColor: "#045004ff",
                    color: "white",
                    padding: "0.5rem 0.75rem",
                    borderRadius: "9999px",
                    border: "none",
                    cursor: "pointer",
                    fontSize: "0.8rem",
                    fontWeight: "600",
                    minWidth: isMobile ? "100%" : "auto",
                    whiteSpace: "nowrap",
                  }}
                >
                  Exportar BD
                </button>

                <button
                  type="button"
                  onClick={() =>
                    window.open(
                      `${API_BASE_URL}/api/export/analitica_contratacion.php?role=${encodeURIComponent(role)}`,
                      "_blank",
                    )
                  }
                  style={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    backgroundColor: "#096916ff",
                    color: "white",
                    padding: "0.5rem 0.75rem",
                    borderRadius: "9999px",
                    border: "none",
                    cursor: "pointer",
                    fontSize: "0.8rem",
                    fontWeight: "600",
                    minWidth: isMobile ? "100%" : "auto",
                    whiteSpace: "nowrap",
                  }}
                >
                  Analítica
                </button>

                <button
                  type="button"
                  onClick={() => window.open("/analitica", "_blank")}
                  style={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    backgroundColor: "#074299ff",
                    color: "white",
                    padding: "0.5rem 0.75rem",
                    borderRadius: "9999px",
                    border: "none",
                    cursor: "pointer",
                    fontSize: "0.8rem",
                    fontWeight: "600",
                    minWidth: isMobile ? "100%" : "auto",
                    whiteSpace: "nowrap",
                  }}
                >
                  Analítica Gráfica
                </button>

                <button
                  type="button"
                  onClick={() => window.open("/plan-desarrollo", "_blank")}
                  style={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    backgroundColor: "#8e9713ff",
                    color: "white",
                    padding: "0.5rem 0.75rem",
                    borderRadius: "9999px",
                    border: "none",
                    cursor: "pointer",
                    fontSize: "0.8rem",
                    fontWeight: "600",
                    minWidth: isMobile ? "100%" : "auto",
                    whiteSpace: "nowrap",
                  }}
                >
                  Plan de Desarrollo
                </button>

                {/* ✅ NUEVO: Indicadores (Plan) */}
                <button
                  type="button"
                  onClick={() => window.open("/plan-desarrollo-indicadores", "_blank")}
                  style={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    backgroundColor: "#9b6666",
                    color: "white",
                    padding: "0.5rem 0.75rem",
                    borderRadius: "9999px",
                    border: "none",
                    cursor: "pointer",
                    fontSize: "0.8rem",
                    fontWeight: "600",
                    minWidth: isMobile ? "100%" : "auto",
                    whiteSpace: "nowrap",
                  }}
                >
                  Indicadores
                </button>

                <button
                  type="button"
                  onClick={() => window.open("/presupuesto", "_blank")}
                  style={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    backgroundColor: "#3b82f6",
                    color: "white",
                    padding: "0.5rem 0.75rem",
                    borderRadius: "9999px",
                    border: "none",
                    cursor: "pointer",
                    fontSize: "0.8rem",
                    fontWeight: "600",
                    minWidth: isMobile ? "100%" : "auto",
                    whiteSpace: "nowrap",
                  }}
                >
                  Presupuesto
                </button>
              </div>
            )}
          </div>
          {/* ✅ IMPORTANTE: ESTE </div> CIERRA "Info usuario y acciones" */}

          {/* Buscador */}
          <div
            style={{
              display: "flex",
              justifyContent: "flex-end",
              width: "100%",
              marginBottom: "1rem",
              padding: isMobile ? "0.75rem" : "1rem",
              boxSizing: "border-box",
            }}
          >
            <div style={{ display: "flex", alignItems: "center", position: "relative" }}>
              {searchVisible && (
                <input
                  type="text"
                  placeholder="Buscar por nombre o radicado..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  style={{
                    padding: "0.5rem 0.75rem",
                    borderRadius: "0.5rem",
                    border: "1px solid #e5e7eb",
                    fontSize: "0.875rem",
                    marginRight: "0.5rem",
                    width: isMobile ? "180px" : "250px",
                    animation: "slideIn 0.3s ease-out",
                  }}
                  autoFocus
                />
              )}
              <button
                onClick={() => {
                  setSearchVisible(!searchVisible)
                  if (searchVisible && searchTerm) setSearchTerm("")
                }}
                style={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  backgroundColor: searchVisible ? "#1e40af" : "#f3f4f6",
                  color: searchVisible ? "white" : "#1e40af",
                  border: "none",
                  borderRadius: "50%",
                  width: "36px",
                  height: "36px",
                  cursor: "pointer",
                  boxShadow: "0 1px 2px rgba(0, 0, 0, 0.1)",
                  transition: "all 0.2s ease",
                }}
              >
                {searchVisible ? (
                  "×"
                ) : (
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    width="18"
                    height="18"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  >
                    <circle cx="11" cy="11" r="8"></circle>
                    <line x1="21" y1="21" x2="16.65" y2="16.65"></line>
                  </svg>
                )}
              </button>
            </div>
          </div>

          <style jsx="true">{`
            @keyframes slideIn {
              from {
                width: 0;
                opacity: 0;
              }
              to {
                width: ${isMobile ? "180px" : "250px"};
                opacity: 1;
              }
            }
          `}</style>

          {/* Tabla de contratos */}
          <div style={{ padding: isMobile ? "0.75rem" : "1.5rem", width: "100%", boxSizing: "border-box" }}>
            {contracts.length === 0 ||
            (searchTerm &&
              contracts.filter(
                (contract) =>
                  contract.nombre_contratista?.toLowerCase().includes(searchTerm.toLowerCase()) ||
                  contract.radicado?.toLowerCase().includes(searchTerm.toLowerCase()),
              ).length === 0) ? (
              <div
                style={{
                  textAlign: "center",
                  padding: "3rem",
                  color: "#6b7280",
                  backgroundColor: "#f9fafb",
                  borderRadius: "0.5rem",
                }}
              >
                <FileText size={48} style={{ margin: "0 auto 1rem", color: "#9ca3af" }} />
                <p>
                  {searchTerm
                    ? "No se encontraron contratos que coincidan con la búsqueda"
                    : "No hay contratos disponibles"}
                </p>
              </div>
            ) : (
              <div style={{ overflowX: isMobile ? "auto" : "visible" }}>
                <table
                  style={{
                    width: "100%",
                    tableLayout: isMobile ? "auto" : "fixed",
                    borderCollapse: "separate",
                    borderSpacing: "0",
                    borderRadius: "0.5rem",
                    overflow: "hidden",
                    boxShadow: "0 1px 3px rgba(0, 0, 0, 0.1)",
                    fontSize: isMobile ? "0.8rem" : "1rem",
                  }}
                >
                  <colgroup>
                    <col style={{ width: columnWidths.fecha }} />
                    <col style={{ width: columnWidths.secretaria }} />
                    <col style={{ width: columnWidths.radicado }} />
                    <col style={{ width: columnWidths.contratista }} />
                    <col style={{ width: columnWidths.estado }} />
                    <col style={{ width: columnWidths.fase }} />
                    <col style={{ width: columnWidths.acciones }} />
                    {["solicitante", "presupuesto", "almacen", "administrador"].includes(role) && (
                      <col style={{ width: "10%" }} />
                    )}
                  </colgroup>

                  <thead>
                    <tr style={{ backgroundColor: "#1e40af", color: "white" }}>
                      <th
                        style={{
                          padding: isMobile ? "0.5rem 0.25rem" : "0.75rem 0.5rem",
                          textAlign: "left",
                          fontSize: isMobile ? "0.7rem" : "0.75rem",
                          fontWeight: "600",
                          textTransform: "uppercase",
                          overflow: "hidden",
                          textOverflow: "ellipsis",
                          whiteSpace: "nowrap",
                        }}
                      >
                        Fecha
                      </th>
                      <th
                        style={{
                          padding: isMobile ? "0.5rem 0.25rem" : "0.75rem 0.5rem",
                          textAlign: "left",
                          fontSize: isMobile ? "0.7rem" : "0.75rem",
                          fontWeight: "600",
                          textTransform: "uppercase",
                          overflow: "hidden",
                          textOverflow: "ellipsis",
                          whiteSpace: "nowrap",
                        }}
                      >
                        Secretaría
                      </th>
                      <th
                        style={{
                          padding: isMobile ? "0.5rem 0.25rem" : "0.75rem 0.5rem",
                          textAlign: "left",
                          fontSize: isMobile ? "0.7rem" : "0.75rem",
                          fontWeight: "600",
                          textTransform: "uppercase",
                          overflow: "hidden",
                          textOverflow: "ellipsis",
                          whiteSpace: "nowrap",
                        }}
                      >
                        Radicado
                      </th>
                      <th
                        style={{
                          padding: isMobile ? "0.5rem 0.25rem" : "0.75rem 0.5rem",
                          textAlign: "left",
                          fontSize: isMobile ? "0.7rem" : "0.75rem",
                          fontWeight: "600",
                          textTransform: "uppercase",
                          overflow: "hidden",
                          textOverflow: "ellipsis",
                          whiteSpace: "nowrap",
                        }}
                      >
                        Nombre del Contratista
                      </th>
                      <th
                        style={{
                          padding: isMobile ? "0.5rem 0.25rem" : "0.75rem 0.5rem",
                          textAlign: "left",
                          fontSize: isMobile ? "0.7rem" : "0.75rem",
                          fontWeight: "600",
                          textTransform: "uppercase",
                          overflow: "hidden",
                          textOverflow: "ellipsis",
                          whiteSpace: "nowrap",
                        }}
                      >
                        Estado
                      </th>
                      <th
                        style={{
                          padding: isMobile ? "0.5rem 0.25rem" : "0.75rem 0.5rem",
                          textAlign: "left",
                          fontSize: isMobile ? "0.7rem" : "0.75rem",
                          fontWeight: "600",
                          textTransform: "uppercase",
                          overflow: "hidden",
                          textOverflow: "ellipsis",
                          whiteSpace: "nowrap",
                        }}
                      >
                        Fase
                      </th>
                      <th
                        style={{
                          padding: isMobile ? "0.5rem 0.25rem" : "0.75rem 0.5rem",
                          textAlign: "left",
                          fontSize: isMobile ? "0.7rem" : "0.75rem",
                          fontWeight: "600",
                          textTransform: "uppercase",
                          overflow: "hidden",
                          textOverflow: "ellipsis",
                          whiteSpace: "nowrap",
                        }}
                      >
                        Acciones
                      </th>

                      {(["solicitante", "presupuesto", "almacen", "administrador"].includes(role) ||
                        (user && isAuthorizedUser(user.email))) && (
                        <th
                          style={{
                            padding: isMobile ? "0.5rem 0.25rem" : "0.75rem 0.5rem",
                            textAlign: "left",
                            fontSize: isMobile ? "0.7rem" : "0.75rem",
                            fontWeight: "600",
                            textTransform: "uppercase",
                            overflow: "hidden",
                            textOverflow: "ellipsis",
                            whiteSpace: "nowrap",
                          }}
                        >
                          Solicitudes
                        </th>
                      )}
                    </tr>
                  </thead>

                  <tbody>
                    {contracts
                      .filter(
                        (contract) =>
                          contract.nombre_contratista?.toLowerCase().includes(searchTerm.toLowerCase()) ||
                          contract.radicado?.toLowerCase().includes(searchTerm.toLowerCase()),
                      )
                      .map((contract, index) => (
                        <tr key={contract.id} style={{ backgroundColor: index % 2 === 0 ? "white" : "#f9fafb" }}>
                          {/* FECHA */}
                          <td
                            style={{
                              padding: isMobile ? "0.5rem 0.25rem" : "0.75rem 0.5rem",
                              fontSize: isMobile ? "0.75rem" : "0.875rem",
                              borderBottom: "1px solid #e5e7eb",
                              overflow: "visible",
                              textOverflow: "unset",
                              whiteSpace: "nowrap",
                            }}
                          >
                            <span
                              style={{
                                display: "inline-flex",
                                alignItems: "center",
                                gap: "6px",
                                justifyContent: "center",
                                width: "100%",
                                color: "#1e40af",
                              }}
                            >
                              <Calendar size={14} />
                              {formatDate(contract.created_at)}
                            </span>
                          </td>

                          {/* SECRETARÍA */}
                          <td
                            style={{
                              padding: isMobile ? "0.5rem 0.25rem" : "0.75rem 0.5rem",
                              fontSize: isMobile ? "0.75rem" : "0.875rem",
                              borderBottom: "1px solid #e5e7eb",
                              overflow: "hidden",
                              textOverflow: "ellipsis",
                              whiteSpace: isMobile ? "normal" : "nowrap",
                            }}
                          >
                            {contract.secretaria || "No especificada"}
                          </td>

                          {/* RADICADO */}
                          <td
                            style={{
                              padding: isMobile ? "0.5rem 0.25rem" : "0.75rem 0.5rem",
                              fontSize: isMobile ? "0.75rem" : "0.875rem",
                              borderBottom: "1px solid #e5e7eb",
                              overflow: "hidden",
                              textOverflow: "ellipsis",
                              whiteSpace: isMobile ? "normal" : "nowrap",
                            }}
                          >
                            {contract.radicado || "Sin radicado"}
                          </td>

                          {/* NOMBRE */}
                          <td
                            style={{
                              padding: isMobile ? "0.5rem 0.25rem" : "0.75rem 0.5rem",
                              fontSize: isMobile ? "0.75rem" : "0.875rem",
                              borderBottom: "1px solid #e5e7eb",
                              overflow: "hidden",
                              textOverflow: "ellipsis",
                              whiteSpace: "nowrap",
                            }}
                            title={contract.nombre_contratista || "Por definir"}
                          >
                            {contract.nombre_contratista || "Por definir"}
                          </td>

                          {/* ESTADO */}
                          <td
                            style={{
                              padding: isMobile ? "0.5rem 0.25rem" : "0.75rem 0.5rem",
                              fontSize: isMobile ? "0.75rem" : "0.875rem",
                              borderBottom: "1px solid #e5e7eb",
                              overflow: "hidden",
                              textOverflow: "ellipsis",
                              whiteSpace: "nowrap",
                            }}
                          >
                            <span
                              style={{
                                display: "inline-block",
                                padding: "0.25rem 0.75rem",
                                borderRadius: "9999px",
                                fontSize: "0.75rem",
                                fontWeight: "600",
                                ...getStatusBadgeStyle(contract.status, contract),
                              }}
                            >
                              {getStatusLabel(contract.status)}
                            </span>
                          </td>

                          {/* FASE */}
                          <td
                            style={{
                              padding: isMobile ? "0.5rem 0.25rem" : "0.75rem 0.5rem",
                              fontSize: isMobile ? "0.75rem" : "0.875rem",
                              borderBottom: "1px solid #e5e7eb",
                              overflow: "hidden",
                              textOverflow: "ellipsis",
                              whiteSpace: "normal",
                              lineHeight: "1.3",
                              maxWidth: isMobile ? "auto" : "150px",
                            }}
                          >
                            {getPhaseText(Number(contract.current_phase), contract.status)}
                          </td>

                          {/* ACCIONES */}
                          <td
                            style={{
                              padding: isMobile ? "0.5rem 0.25rem" : "0.75rem 0.5rem",
                              fontSize: isMobile ? "0.75rem" : "0.875rem",
                              borderBottom: "1px solid #e5e7eb",
                              textAlign: "center",
                            }}
                          >
                            <div
                              style={{
                                display: "flex",
                                flexDirection: isMobile ? "column" : "row",
                                gap: "0.5rem",
                                justifyContent: "center",
                              }}
                            >
                              <Link
                                to={`/contratos/${contract.id}`}
                                style={{
                                  display: "inline-flex",
                                  alignItems: "center",
                                  justifyContent: "center",
                                  backgroundColor: canEditContract(contract) ? "#1e40af" : "#6b7280",
                                  color: "white",
                                  padding: isMobile ? "0.4rem 0.5rem" : "0.5rem 0.75rem",
                                  borderRadius: "0.375rem",
                                  textDecoration: "none",
                                  fontSize: isMobile ? "0.7rem" : "0.75rem",
                                  fontWeight: "500",
                                  whiteSpace: "nowrap",
                                  minWidth: isMobile ? "auto" : "80px",
                                }}
                              >
                                {canEditContract(contract) ? (
                                  <>
                                    <Edit size={14} style={{ marginRight: "0.25rem" }} />
                                    Editar
                                  </>
                                ) : (
                                  "Ver detalles"
                                )}
                              </Link>

                              {role === "administrador" && (
                                <>
                                  {deleteConfirm === contract.id ? (
                                    <div
                                      style={{
                                        display: "flex",
                                        flexDirection: isMobile ? "column" : "row",
                                        gap: "0.25rem",
                                      }}
                                    >
                                      <button
                                        type="button"
                                        onClick={(e) => {
                                          e.preventDefault()
                                          e.stopPropagation()
                                          deleteContract(contract.id)
                                        }}
                                        style={{
                                          display: "inline-flex",
                                          alignItems: "center",
                                          justifyContent: "center",
                                          backgroundColor: "#ef4444",
                                          color: "white",
                                          padding: isMobile ? "0.4rem 0.5rem" : "0.5rem 0.75rem",
                                          borderRadius: "0.375rem",
                                          border: "none",
                                          fontSize: isMobile ? "0.7rem" : "0.75rem",
                                          fontWeight: "500",
                                          cursor: "pointer",
                                          minWidth: isMobile ? "auto" : "80px",
                                        }}
                                      >
                                        Confirmar
                                      </button>

                                      <button
                                        type="button"
                                        onClick={(e) => {
                                          e.preventDefault()
                                          e.stopPropagation()
                                          setDeleteConfirm(null)
                                        }}
                                        style={{
                                          display: "inline-flex",
                                          alignItems: "center",
                                          justifyContent: "center",
                                          backgroundColor: "#6b7280",
                                          color: "white",
                                          padding: isMobile ? "0.4rem 0.5rem" : "0.5rem 0.75rem",
                                          borderRadius: "0.375rem",
                                          border: "none",
                                          fontSize: isMobile ? "0.7rem" : "0.75rem",
                                          fontWeight: "500",
                                          cursor: "pointer",
                                          minWidth: isMobile ? "auto" : "80px",
                                        }}
                                      >
                                        Cancelar
                                      </button>
                                    </div>
                                  ) : (
                                    <button
                                      type="button"
                                      onClick={(e) => {
                                        e.preventDefault()
                                        e.stopPropagation()
                                        setDeleteConfirm(contract.id)
                                      }}
                                      style={{
                                        display: "inline-flex",
                                        alignItems: "center",
                                        justifyContent: "center",
                                        backgroundColor: "#ef4444",
                                        color: "white",
                                        padding: isMobile ? "0.4rem 0.5rem" : "0.5rem 0.75rem",
                                        borderRadius: "0.375rem",
                                        border: "none",
                                        fontSize: isMobile ? "0.7rem" : "0.75rem",
                                        fontWeight: "500",
                                        cursor: "pointer",
                                        minWidth: isMobile ? "auto" : "80px",
                                      }}
                                      title="Eliminar contrato (solo administrador)"
                                    >
                                      <Trash2 size={14} style={{ marginRight: "0.25rem" }} />
                                      Eliminar
                                    </button>
                                  )}
                                </>
                              )}
                            </div>
                          </td>

                          {/* SOLICITUDES */}
                          {(["solicitante", "presupuesto", "almacen", "administrador"].includes(role) ||
                            (user && isAuthorizedUser(user.email))) && (
                            <td
                              style={{
                                padding: isMobile ? "0.5rem 0.25rem" : "0.75rem 0.5rem",
                                fontSize: isMobile ? "0.75rem" : "0.875rem",
                                borderBottom: "1px solid #e5e7eb",
                                textAlign: "center",
                              }}
                            >
                              <button
                                onClick={() => handleViewRequests(contract)}
                                style={{
                                  display: "inline-flex",
                                  alignItems: "center",
                                  justifyContent: "center",
                                  padding: isMobile ? "0.4rem 0.5rem" : "0.5rem 0.75rem",
                                  borderRadius: "0.375rem",
                                  border: "none",
                                  fontSize: isMobile ? "0.7rem" : "0.75rem",
                                  fontWeight: "700",
                                  cursor: "pointer",
                                  minWidth: isMobile ? "auto" : "110px",
                                  height: isMobile ? "auto" : "2.25rem",
                                  lineHeight: "1.25rem",
                                  whiteSpace: "nowrap",
                                  ...getSolicitudesButtonStyle(contract),
                                }}
                              >
                                Ver solicitudes
                              </button>
                            </td>
                          )}
                        </tr>
                      ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Modal para solicitudes CDP/RP (Presupuesto) */}
      {showSolicitudModal && selectedContract && role !== "almacen" && (
        <PresupuestoSolicitudesModal
          show={showSolicitudModal}
          contrato={selectedContract}
          onClose={() => {
            setShowSolicitudModal(false)
            fetchContracts(role, user)
          }}
        />
      )}

      {/* Modal para solicitudes de Almacén */}
      {showSolicitudModal && selectedContract && role === "almacen" && (
        <AlmacenCertificadoModal
          isOpen={showSolicitudModal}
          onClose={() => setShowSolicitudModal(false)}
          contractData={{
            ...selectedContract,
            almacen_request_id: selectedContract.almacen_requests?.[0]?.id || null,
          }}
          userRole={role}
          // ✅ importante: acepta (reqId, info) para saber si fue aprobado/negado + adjuntos
          onResponded={(reqId, info) => {
            handleResponderAlmacen(reqId, selectedContract, info || {})
          }}
        />
      )}

      {showResponderAlmacen && solicitudSeleccionada && (
        <EnviarAlmacen
          userRole={role}
          solicitud={solicitudSeleccionada}
          onClose={() => {
            setShowResponderAlmacen(false)
            setSolicitudSeleccionada(null)
          }}
          onSubmit={handleResponderAlmacen}
        />
      )}

      {phaseModalOpen && (
        <div
          className="modal"
          style={{
            position: "fixed",
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: "rgba(0,0,0,0.5)",
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            zIndex: 9999,
          }}
        >
          <div
            style={{
              backgroundColor: "#fff",
              borderRadius: "8px",
              width: "90%",
              maxWidth: "800px",
              padding: "20px",
              boxShadow: "0 4px 10px rgba(0,0,0,0.3)",
            }}
          >
            <h3>Historial de fases - {phaseModalNombre}</h3>

            <table className="table table-striped mt-3">
              <thead>
                <tr>
                  <th>Fase</th>
                  <th>Fecha Inicio</th>
                  <th>Fecha Fin</th>
                  <th>Días</th>
                </tr>
              </thead>
              <tbody>
                {phaseModalRows.length ? (
                  phaseModalRows.map((f, i) => (
                    <tr key={i}>
                      <td>{f.phase_name || f.phase}</td>
                      <td>{f.fecha_inicio || ""}</td>
                      <td>{f.fecha_fin || ""}</td>
                      <td>{f.dias || 0}</td>
                    </tr>
                  ))
                ) : (
                  <tr>
                    <td colSpan="4" style={{ textAlign: "center" }}>
                      No hay historial disponible.
                    </td>
                  </tr>
                )}
              </tbody>
            </table>

            <div style={{ textAlign: "right", marginTop: "10px" }}>
              <strong>Total hasta Alcalde: {phaseModalTotal} días</strong>
            </div>

            <div style={{ textAlign: "center", marginTop: "20px" }}>
              <button className="btn btn-primary" onClick={() => setPhaseModalOpen(false)}>
                Cerrar
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}
